import { useAccessPendingAction } from "./use-access-pending-action";
import { useSearchParams } from "react-router";
import { accessScopeLabel } from "@/lib/access-scope-label";
import { useRef, useState } from "react";
import { useApolloClient, useMutation, useQuery } from "@apollo/client/react";
import { useIntl } from "react-intl";
import { Navigate } from "react-router";
import { BoxHeader } from "@/components/design/BoxHeader";
import { Button } from "@/components/design/Button";
import { AdminMasterDetail } from "@/components/admin/AdminMasterDetail";
import { MenuItem } from "@/components/design/MenuItem";
import { KpiBadge } from "@/components/design/KpiBadge";
import { Disclosure } from "@/components/design/Disclosure";
import { InfoTooltip } from "@/components/design/InfoTooltip";
import { SegmentedControl } from "@/components/design/SegmentedControl";
import { AccessRoleIcon } from "./AccessRoleIcon";
import { AccessRolePermissions } from "./AccessRolePermissions";
import { roleLabel, roleDescription, isProvidedRole, providedRoleOrder } from "./access-role-labels";
import { permissionLabel } from "./access-permission-labels";
import { AccessPermissionCatalog } from "./AccessPermissionCatalog";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { UnsavedChangesDialog } from "@/components/common/UnsavedChangesDialog";
import { usePendingScope } from "@/lib/pending-edits";
import { useUnsavedEditsGate } from "@/lib/unsaved-edits-gate";
import { LocalizedError } from "@/lib/localized-error";
import { TextInput } from "@/components/design/TextField";
import { AccessRoleImpactDocument, AccessRoleCatalogDocument, CreateAccessRoleDocument, UpdateAccessRoleDocument, DeleteAccessRoleDocument,
  type AccessRoleDetailsFragment } from "@/graphql/generated/operations";
import { errorClassification, userErrorMessage } from "@/lib/user-error";
import { AccessRoleImpactDialog } from "./AccessRoleImpactDialog";
import { AccessRoleAssignmentMembers } from "./AccessRoleAssignmentMembers";

export function AccessRolesPanel() {
  const intl = useIntl();
  const apollo = useApolloClient();
  const [reviewing, setReviewing] = useState(false);
  const reviewDecision = useRef<((confirmed: boolean) => void) | null>(null);
  const t = (id: string, values?: Record<string, string | number>) => intl.formatMessage({ id }, values);
  const { data, loading, error } = useQuery(AccessRoleCatalogDocument, { fetchPolicy: "network-only" });
  const [createRole, { loading: createPending }] = useMutation(CreateAccessRoleDocument);
  const [updateRole, { loading: updatePending }] = useMutation(UpdateAccessRoleDocument);
  const [deleteRole, { loading: deletePending }] = useMutation(DeleteAccessRoleDocument);
  const saving = createPending || updatePending || deletePending || reviewing;
  const [creating, setCreating] = useState(false);
  const [tab, setTab] = useState("permissions");
  const [showCatalog, setShowCatalog] = useState(false);
  const [editing, setEditing] = useState<AccessRoleDetailsFragment | null>(null);
  const [deleting, setDeleting] = useState<AccessRoleDetailsFragment | null>(null);
  const formOpen = creating || !!editing;
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selected, setSelected] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  const [saveError, setSaveError] = useState<string | null>(null);
  const [createdId, setCreatedId] = useState<string | null>(null);
  const [params] = useSearchParams();
  const [activeRoleId, setActiveRoleId] = useState<string | null>(params.get("roleId"));
  const submitInFlight = useRef(false);
  const savePromise = useRef<Promise<void> | null>(null);
  const leaveGate = useUnsavedEditsGate();
  const deletion = useAccessPendingAction("access-role-delete");
  const createButton = useRef<HTMLButtonElement>(null);
  const permissions = data?.accessPermissions.permissions ?? [];
  const roles = [...(data?.accessRoles.roles ?? [])]
    .filter((role) => !role.isSystem || role.isInUse || role.assignments.length > 0 || role.assignedPersonCount > 0)
    .sort((a, b) =>
    providedRoleOrder(a) - providedRoleOrder(b) || Number(a.isSystem) - Number(b.isSystem)
    || roleLabel(intl, a).localeCompare(roleLabel(intl, b), intl.locale, { sensitivity: "base", numeric: true })
    || a.id.localeCompare(b.id));
  const activeRole = roles.find((role) => role.id === activeRoleId) ?? roles[0];
  const hasDraft = !!name || !!description || selected.length > 0;
  const dirty = editing
    ? name !== roleLabel(intl, editing) || description !== (roleDescription(intl, editing) ?? "")
      || selected.length !== editing.permissions.length
      || editing.permissions.some((permission) => !selected.includes(permission.id))
    : hasDraft;
  const query = search.trim().toLocaleLowerCase(intl.locale);
  const visiblePermissions = (formOpen ? permissions : activeRole?.permissions ?? [])
    .filter((permission) => [permission.code, permission.description, permissionLabel(intl, permission.code, permission.description)]
      .some((value) => value.toLocaleLowerCase(intl.locale).includes(query)))
    .sort((a, b) => a.code.localeCompare(b.code, intl.locale));
  usePendingScope("access-role-editor", { dirty, awaitingConfirmation: reviewing, save: submit, discard: closeForm });
  const managedPermission = permissions.find(permission => permission.code === "read:managed-information");
  const checkManagedAddition = !!editing?.assignments.length && !!managedPermission && !selected.includes(managedPermission.id);
  const managedAddition = useQuery(AccessRoleImpactDocument, {
    variables: { roleId: editing?.id ?? "", permissionIds: [...selected, ...(managedPermission ? [managedPermission.id] : [])],
      groupsAfter: null, peopleAfter: null },
    skip: !checkManagedAddition, fetchPolicy: "network-only",
  });
  const managedUnavailable = checkManagedAddition && (managedAddition.loading || !!managedAddition.error || !managedAddition.data?.accessRoleImpact.canSave);
  const permissionRestrictions: Record<string, string> = managedUnavailable && managedPermission ? {
    [managedPermission.id]: managedAddition.loading ? t("admin.roles.managedChecking")
      : managedAddition.error ? t("admin.roles.managedCheckFailed") : t("admin.roles.managedIncompatible"),
  } : {};

  function closeForm() {
    setCreating(false);
    setName("");
    setDescription("");
    setSelected([]);
    setEditing(null);
    setSearch("");
    setSaveError(null);
    window.requestAnimationFrame(() => createButton.current?.focus());
  }

  function beginEdit(role: AccessRoleDetailsFragment) {
    if (!role.canUpdate || saving) return;
    setActiveRoleId(role.id);
    setTab("permissions");
    setCreating(false);
    setEditing(role);
    setName(roleLabel(intl, role));
    setDescription(roleDescription(intl, role) ?? "");
    setSelected(role.permissions.map((permission) => permission.id));
    setSearch("");
    setSaveError(null);
    setCreatedId(null);
  }

  function submit(): Promise<void> {
    // SaveBar and a leave dialog can ask for the same save while a mutation is pending.
    // Both must await its outcome, never treat an in-flight write as a successful no-op.
    if (savePromise.current) return savePromise.current;
    const promise = persistDraft().finally(() => { savePromise.current = null; });
    savePromise.current = promise;
    return promise;
  }

  async function persistDraft() {
    if (!dirty) return;
    if (!name.trim() || name.trim().length > 120 || description.trim().length > 2000) {
      throw new LocalizedError(t("admin.roles.invalidFields"));
    }
    if (!selected.length || !selected.every((id) => permissions.some((permission) => permission.id === id))) {
      throw new LocalizedError(t("admin.roles.permissionsRequired"));
    }
    if (loading || error || submitInFlight.current) throw new LocalizedError(t("common.saveFailed"));
    submitInFlight.current = true;
    setSaveError(null);
    try {
      if (editing) {
        setReviewing(true);
        const confirmed = await new Promise<boolean>((resolve) => { reviewDecision.current = resolve; });
        setReviewing(false);
        reviewDecision.current = null;
        if (!confirmed) throw new LocalizedError(t("admin.roles.impactCancelled"));
        const result = await updateRole({
          variables: { input: { id: editing.id, name: name.trim(),
            description: description.trim() || null, permissionIds: selected } },
          update(cache, { data: response }) {
            if (!response?.updateAccessRole) return;
            cache.updateQuery({ query: AccessRoleCatalogDocument }, (previous) => previous ? {
              ...previous, accessRoles: { ...previous.accessRoles,
                roles: previous.accessRoles.roles.map((role) => role.id === response.updateAccessRole.id ? response.updateAccessRole : role) },
            } : previous);
          },
        });
        if (!result.data?.updateAccessRole) throw new LocalizedError(t("common.saveFailed"));
        void apollo.refetchQueries({ include: "active" }).catch(() => undefined);
        closeForm();
        return;
      }
      const result = await createRole({
        variables: { input: { name: name.trim(), description: description.trim() || null, permissionIds: selected } },
        update(cache, { data: response }) {
          if (!response?.createAccessRole) return;
          cache.updateQuery({ query: AccessRoleCatalogDocument }, (previous) => previous ? {
            ...previous,
            accessRoles: { ...previous.accessRoles, roles: [response.createAccessRole,
              ...previous.accessRoles.roles.filter((role) => role.id !== response.createAccessRole.id)] },
          } : previous);
        },
      });
      if (result.data?.createAccessRole) {
        setCreatedId(result.data.createAccessRole.id);
        setActiveRoleId(result.data.createAccessRole.id);
        closeForm();
      } else throw new LocalizedError(t("common.saveFailed"));
    } catch (cause) {
      const message = cause instanceof LocalizedError ? cause.message : userErrorMessage(intl, cause);
      setSaveError(message);
      throw new LocalizedError(message, { cause });
    } finally {
      submitInFlight.current = false;
    }
  }

  async function confirmDelete() {
    if (!deleting?.canDelete || submitInFlight.current) return;
    submitInFlight.current = true;
    setSaveError(null);
    try {
      const target = deleting;
      const result = await deleteRole({ variables: { input: { id: target.id } },
        update(cache, { data: response }) {
          if (!response?.deleteAccessRole) return;
          cache.updateQuery({ query: AccessRoleCatalogDocument }, (previous) => previous ? {
            ...previous, accessRoles: { ...previous.accessRoles, roles: previous.accessRoles.roles.filter((role) => role.id !== target.id) },
          } : previous);
        },
      });
      if (result.data?.deleteAccessRole) { setDeleting(null); setActiveRoleId(null); setCreatedId(null); }
    } catch (cause) {
      setSaveError(userErrorMessage(intl, cause));
      throw cause;
    } finally { submitInFlight.current = false; }
  }

  // Formular und Detail rendern nie gleichzeitig — dieselbe Kennung ist hier
  // kein Duplikat, sondern derselbe Platz in zwei Zuständen.
  const quickFilter = <TextInput id="roles-search" type="search" aria-label={t("admin.roles.search")} placeholder={t("admin.roles.search")}
    value={search} onInput={(event) => setSearch(event.currentTarget.value)}
    onChange={(event) => setSearch(event.target.value)}
    onKeyDown={(event) => { if (event.key === "Enter") event.preventDefault(); }} />;

  if (error && errorClassification(error) === "FORBIDDEN") return <Navigate to="/" replace />;

  return (
    <AdminMasterDetail title={t("admin.roles.listTitle")} count={roles.length} list={<>
      <Button id="roles-add" ref={createButton} className="mb-4 w-full" aria-label={t(hasDraft && !formOpen ? "admin.roles.resumeDraft" : "admin.roles.new")}
        disabled={loading || !!error || formOpen || saving || permissions.length === 0}
        onClick={() => { const open = () => { setCreating(true); setShowCatalog(false); setCreatedId(null); }; if (deletion.pending) leaveGate.request(open); else open(); }}>
        {hasDraft && !formOpen ? t("admin.roles.resumeDraft") : t("admin.roles.addRole")}
      </Button>
      <Button id="roles-catalog" variant="secondary" className="mb-4 w-full" disabled={loading || !!error || saving || formOpen}
        onClick={() => { if (deletion.pending) leaveGate.request(() => setShowCatalog(true)); else setShowCatalog(true); }}>{t("admin.roles.catalog")}</Button>
      {loading && <p role="status" className="text-smycles-body text-ink-muted">{t("admin.roles.loading")}</p>}
      {error && <p role="alert" className="text-smycles-body text-ink">{userErrorMessage(intl, error)}</p>}
      {!loading && !error && roles.length === 0 && <p className="text-smycles-body text-ink">{t("admin.roles.empty")}</p>}
      {!error && <nav id="roles-list" aria-label={t("admin.roles.existing")} className="space-y-1">
        {["provided", "custom", "system"].map((category) => {
          const group = roles.filter((role) => (isProvidedRole(role) ? "provided" : role.isSystem ? "system" : "custom") === category);
          return group.length > 0 && <Disclosure key={category} id={`roles-group-${category}`} className="mb-4" defaultOpen
            title={<span role="heading" aria-level={3}>{t(category === "provided" ? "admin.groups.systemGroups" : category === "system" ? "admin.roles.systemRoles" : "admin.roles.customRoles")}</span>} count={group.length}>
            {group.map((role) => <MenuItem key={role.id} icon={<AccessRoleIcon role={role} />} id={`roles-item-${role.id}`} aria-label={roleLabel(intl, role)} title={roleLabel(intl, role)}
              aria-description={t("admin.roles.peopleCount", { count: role.assignedPersonCount })}
              active={!creating && !showCatalog && role.id === activeRole?.id} disabled={saving || !!editing}

              onClick={() => { setActiveRoleId(role.id); setShowCatalog(false); setTab("permissions"); setCreating(false); setCreatedId(null); setSaveError(null); }}>
              <span id={`roles-item-${role.id}-name`} className="block whitespace-normal break-words">{roleLabel(intl, role)}</span>
              <span className="mt-1 block text-smycles-meta tabular-nums text-ink-muted">{t("admin.roles.peopleCount", { count: role.assignedPersonCount })}</span>
            </MenuItem>)}
          </Disclosure>;
        })}
      </nav>}
    </>}>

      {formOpen && (
        <section id="roles-form" className="box min-w-0 bg-surface p-6" aria-label={t(editing ? "admin.roles.edit" : "admin.roles.new")}>
          <BoxHeader title={t(editing ? "admin.roles.edit" : "admin.roles.new")} />
          <form onSubmit={(event) => event.preventDefault()}>
            <fieldset disabled={saving} className="space-y-6">
              <div className="grid grid-cols-1 gap-4">
              <label className="block min-w-0">
                <span className="smycles-field-label">{t("admin.roles.name")}</span>
                <TextInput id="roles-name" autoFocus required maxLength={120} value={name} className="mt-1"
                  placeholder={t("admin.roles.namePlaceholder")} onChange={(event) => setName(event.target.value)} />
              </label>
              <label className="block min-w-0">
                <span className="smycles-field-label">{t("admin.roles.description")}</span>
                <TextInput id="roles-description" maxLength={2000} value={description} className="mt-1"
                  placeholder={t("admin.roles.descriptionPlaceholder")}
                  onChange={(event) => setDescription(event.target.value)} />
              </label>
              </div>
              <fieldset className="space-y-4 border-t border-ink/10 pt-6">
                <legend className="float-left mb-4 w-full text-smycles-body font-medium">{t("admin.roles.assignPermissions")}</legend>
                <div className="clear-both">{quickFilter}</div>
                <AccessRolePermissions permissions={visiblePermissions} restrictions={permissionRestrictions} selected={selected} expandAll={!!query}
                  onToggle={id => setSelected(previous => previous.includes(id) ? previous.filter(value => value !== id) : [...previous, id])} />
                {visiblePermissions.length === 0 && <div className="py-8 text-smycles-body text-ink">
                  <p>{t("admin.roles.noResults")}</p>
                  <Button type="button" variant="ghost" className="mt-2" onClick={() => setSearch("")}>{t("admin.roles.clearSearch")}</Button>
                </div>}
              </fieldset>
              <div className="space-y-2 border-t border-ink/10 pt-4">
                <p id="roles-selectedcount" role="status" className="text-smycles-body font-medium">{t("admin.roles.selected", { count: selected.length })}</p>
                <p className="max-w-[42rem] text-smycles-meta leading-relaxed text-ink">{t(editing ? "admin.roles.editAffectsHolders" : "admin.roles.unassigned")}</p>
              </div>
              {saveError && <p role="alert" className="text-smycles-body text-ink">{saveError}</p>}
              <div className="flex flex-wrap justify-end gap-4">
                <Button type="button" variant="secondary" onClick={() => leaveGate.request(closeForm)}>{t("common.cancel")}</Button>
              </div>
            </fieldset>
          </form>
        </section>
      )}

      {data && !error && !formOpen && showCatalog && <AccessPermissionCatalog permissions={permissions} roles={roles}
        onOpenRole={(id) => { setActiveRoleId(id); setShowCatalog(false); setTab("permissions"); }}
        onCreateRole={(ids) => leaveGate.request(() => { setSelected(ids); setName(""); setDescription(""); setSearch(""); setShowCatalog(false); setCreating(true); setCreatedId(null); })} />}
      {data && !error && !formOpen && !showCatalog && (
        <section id="roles-detail" className="box min-w-0 bg-surface p-6" aria-label={t("admin.roles.details")}>
          <BoxHeader title={<span className="break-words">{activeRole ? roleLabel(intl, activeRole) : t("admin.roles.title")}</span>}
            className="flex-wrap"
            right={activeRole && !activeRole.canUpdate ? <KpiBadge id="roles-readonly" tone="muted"
              info={{ title: t("admin.roles.readOnly"), ariaLabel: t("admin.roles.readOnly"), content: t("admin.roles.systemProtected") }}>
              {t("admin.roles.readOnly")}
            </KpiBadge> : activeRole?.canUpdate ? <div className="flex flex-wrap gap-2">
              <Button id="roles-edit" variant="secondary" size="compact" disabled={saving} onClick={() => leaveGate.request(() => beginEdit(activeRole))}>{t("admin.roles.edit")}</Button>
              {activeRole.canDelete && <Button id="roles-delete" variant="ghost" size="compact" disabled={saving} onClick={() => leaveGate.request(() => { setDeleting(activeRole); setSaveError(null); })}>{t("admin.roles.delete")}</Button>}
            </div> : undefined} />
          {activeRole ? <>
            {createdId === activeRole.id && <p role="status" className="mb-4 text-smycles-body text-ink">{t("admin.roles.created")}</p>}
            {roleDescription(intl, activeRole) && <p className="mb-6 text-smycles-body text-ink">{roleDescription(intl, activeRole)}</p>}
            <SegmentedControl id="roles-sections" className="mb-6" value={tab} onChange={setTab} aria-label={t("admin.roles.sections")}
              options={[{ value: "permissions", label: t("admin.roles.permissions") }, { value: "assignments", label: t("admin.roles.assignments") }]} />
            {tab === "permissions" && <>
            <h3 className="mb-4 text-smycles-body font-medium">{t("admin.roles.permissions")} <span className="text-ink-muted">· {activeRole.permissions.length}</span></h3>
            {quickFilter}
            {query && <p role="status" className="mt-2 text-smycles-meta text-ink-muted">{t("admin.roles.shown", { shown: visiblePermissions.length, total: activeRole.permissions.length })}</p>}
            <AccessRolePermissions permissions={visiblePermissions} expandAll={!!query} />
            {visiblePermissions.length === 0 && <div className="space-y-2 py-4">
              <p className="text-smycles-body text-ink">{t("admin.roles.noRoleResults")}</p>
              {query && <Button variant="ghost" onClick={() => setSearch("")}>{t("admin.roles.clearSearch")}</Button>}
            </div>}
            </>}
            {tab === "assignments" && <section id="roles-assignments" aria-label={t("admin.roles.assignments")} className="mt-6">
              <div className="mb-4 flex items-center gap-2 text-smycles-body text-ink">
                <span>{t("admin.roles.peopleCount", { count: activeRole.assignedPersonCount })}</span>
                <InfoTooltip id="roles-assignmentsinfo" ariaLabel={t("admin.roles.assignments")}>{t("admin.roles.assignmentsHint")}</InfoTooltip>
              </div>
              {activeRole.assignments.length === 0 ? <p className="mt-2 text-smycles-meta text-ink">{t("admin.roles.noAssignments")}</p>
                : <ul className="mt-4 space-y-2">{activeRole.assignments.map((assignment) =>
                  <li key={`${assignment.id}-${assignment.groupId}`}>
                    <AccessRoleAssignmentMembers id={`roles-assignment-${assignment.id}`} roleId={activeRole.id}
                      groupId={assignment.groupId} groupName={assignment.groupName}
                      scopeName={accessScopeLabel(assignment.scope, intl)} personCount={assignment.personCount} />

                  </li>)}</ul>}
            </section>}
          </> : <p className="text-smycles-body text-ink">{t("admin.roles.intro")}</p>}
        </section>
      )}
      {reviewing && editing && <AccessRoleImpactDialog roleId={editing.id} permissionIds={selected}
        onConfirm={() => reviewDecision.current?.(true)} onCancel={() => reviewDecision.current?.(false)} />}
      <UnsavedChangesDialog open={leaveGate.blocked} saving={leaveGate.saving} error={leaveGate.error}
        onStay={leaveGate.stay} onDiscard={leaveGate.discardAndProceed} onSave={leaveGate.saveAndProceed} />
      {deletion.label && <p role="status" className="text-smycles-body font-medium">{deletion.label}</p>}
      <ConfirmDialog id="roles-deletedialog" open={!!deleting} title={t("admin.roles.delete")}
        message={<><p>{t("admin.roles.deleteConfirm", { name: deleting?.name ?? "" })}</p>
          {saveError && <p role="alert" className="mt-4">{saveError}</p>}</>}
        confirmLabel={t(deletePending ? "admin.roles.deleting" : "admin.roles.delete")}
        onCancel={() => { if (!submitInFlight.current) { setDeleting(null); setSaveError(null); } }}
        onConfirm={() => { if (deleting) { deletion.stage(`${t("admin.roles.delete")}: ${roleLabel(intl, deleting)}`, confirmDelete); setDeleting(null); } }} />
    </AdminMasterDetail>
  );
}
